import { Inject, Injectable, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Apollo, gql } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { LICENCE } from '../../global.tokens';
import { formatLocaleCMS } from '../../utils/format-locale-cms';

export interface AcceptTerm {
  name: string;
  termContent: string;
  rejectRedirectUrl?: string;
  license?: string;
  locale: string;
}

interface AcceptedTermRecord {
  locale: string;
  acceptedAt: string;
}

const query = gql`query AcceptTerms($locale: Locale!, $licences: [Licence!]) {
  accept_Terms(locales: [$locale, en], where: { license_in: $licences }) {
    name
    termContent
    rejectRedirectUrl
    license
    locale
  }
}`;

const STORAGE_KEY = 'accepted-terms';
const BASE_CMS_LOCALE = 'en';

@Injectable({
  providedIn: 'root'
})
export class AcceptTermsService {

  private cmsLocale = formatLocaleCMS(this.locale);

  constructor(
    private apollo: Apollo,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(LICENCE) private licence: string,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  getPendingTerms(): Observable<AcceptTerm[]> {
    if (!isPlatformBrowser(this.platformId) || this.cmsLocale === BASE_CMS_LOCALE) {
      return of([]);
    }
    return this.apollo.query<{ accept_Terms: AcceptTerm[] }>({
      query,
      variables: { locale: this.cmsLocale, licences: [this.licence, 'all'] },
      fetchPolicy: 'no-cache',
    }).pipe(
      take(1),
      map(resp => (resp.data?.accept_Terms || []).filter(term =>
        term.locale === this.cmsLocale && !this.isAccepted(term.name)
      )),
      catchError(() => of([])),
    );
  }

  accept(term: AcceptTerm): void {
    const accepted = this.readAccepted();
    accepted[term.name] = { locale: term.locale, acceptedAt: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(accepted));
    } catch { }
  }

  private isAccepted(name: string): boolean {
    return !!this.readAccepted()[name];
  }

  private readAccepted(): Record<string, AcceptedTermRecord> {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  }
}
